import React, { useState, useEffect, useCallback } from "react";
import { useImages, usePreloadDataImages, AccommodationLightbox, getRoomImages } from "@agape/frontend-utils";
import { useAccommodations } from "../contexts/AccommodationContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Users, Maximize2 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { createPageUrl } from "../utils";
import { ACCESSIBILITY_OVERLAYS_ENABLED, BOOKINGS_APP_URL } from "../config/constants";
import anoViannosBackground from "../assets/ano-viannos-background.jpg";
import { useLanguage } from "../contexts/LanguageProvider";
import { AmenityList } from "@agape/frontend-utils";
import {
  getAccommodationDisplayName,
  sortAccommodationsAlphabetically
} from "../utils/accommodationUtils";

// Component for expandable description with read more/less functionality
function ExpandableDescription({ description, maxLength = 150 }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!description) return null;
  
  const shouldTruncate = description.length > maxLength;
  const displayText = shouldTruncate && !isExpanded 
    ? description.slice(0, maxLength).trim() + "..."
    : description;
  
  return (
    <div className="flex-grow">
      <p className="text-gray-600 mb-2 text-base sm:text-lg leading-relaxed">
        {displayText}
      </p>
      {shouldTruncate && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium focus:outline-none focus:underline"
        >
          {isExpanded ? "Read less" : "Read more"}
        </button>
      )}
    </div>
  );
}

export default function Accommodations() {
  const { accommodations, loading: loadingAccommodations } = useAccommodations();
  const { images: allImages, loading: loadingImages } = useImages();
  const { t } = useLanguage();
  const location = useLocation();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedAccommodationImages, setSelectedAccommodationImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedRoomName, setSelectedRoomName] = useState('');

  const [displayAccommodations, setDisplayAccommodations] = useState([]);

  // Handle scrolling to specific accommodation if hash is present in URL
  useEffect(() => {
    if (!loadingAccommodations && displayAccommodations.length > 0 && location.hash) {
      // Small timeout to allow the layout to settle and images to take up space
      const timeoutId = setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          // Calculate offset to account for fixed header if you have one, 
          // but smooth scroll usually does a decent job on its own
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  }, [loadingAccommodations, displayAccommodations.length, location.hash]);

  // Preload accommodation main images for better performance
  usePreloadDataImages(accommodations, {
    imageProperty: 'main_image',
    enabled: !loadingAccommodations
  });

  // Preload all gallery images for better lightbox performance
  usePreloadDataImages(allImages, {
    imageProperty: 'url',
    enabled: !loadingImages
  });

  const openLightbox = useCallback((accommodation, accommodationName) => {
    if (!accommodation?.photos_storage_path) {
      alert("No images found for this accommodation.");
      return;
    }

    const accommodationImages = getRoomImages(accommodation, allImages);

    if (accommodationImages.length > 0) {
      setSelectedAccommodationImages(accommodationImages);
      setSelectedRoomName(accommodationName || '');
      setCurrentIndex(0);
      setLightboxOpen(true);
    } else {
      alert("No images found for this accommodation.");
    }
  }, [allImages]);

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    setSelectedAccommodationImages([]);
    setCurrentIndex(0);
  }, []);

  const navigateImage = useCallback((direction) => {
    const newIndex = direction === 'next'
      ? (currentIndex + 1) % selectedAccommodationImages.length
      : (currentIndex - 1 + selectedAccommodationImages.length) % selectedAccommodationImages.length;
    setCurrentIndex(newIndex);
  }, [currentIndex, selectedAccommodationImages.length]);

  useEffect(() => {
    if (accommodations.length > 0) {
      const updatedAccommodations = accommodations.map(accommodation => {
        if (!accommodation.main_image && accommodation.photos_storage_path && allImages.length > 0) {
          const defaultImage = allImages.find(image => image.category === accommodation.photos_storage_path);
          return { ...accommodation, main_image: defaultImage ? defaultImage.url : null };
        }
        return accommodation;
      });
      setDisplayAccommodations(updatedAccommodations);
    }
  }, [accommodations, allImages]);

  // Sort accommodations lexicographically using shared utility
  const filteredAccommodations = sortAccommodationsAlphabetically(displayAccommodations);



  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center">
        <div className="absolute inset-0 z-0">
          <img
            src={anoViannosBackground}
            alt="Ano Viannos village"
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${ACCESSIBILITY_OVERLAYS_ENABLED ? 'bg-gradient-to-r from-gray-900/85 to-gray-800/75' : 'bg-gradient-to-r from-gray-900/35 to-gray-800/25'}`}></div>
        </div>

        <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4 flex items-center justify-center h-full">
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight px-6 py-2 rounded-lg inline-block bg-black/60">
            Our <span className="text-white">Accommodations</span>
          </h1>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">

        {/* Accommodations Grid */}
        {loadingAccommodations ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white rounded-2xl shadow-lg animate-pulse overflow-hidden">
                <div className="h-64 sm:h-80 bg-gray-200"></div>
                <div className="p-6 sm:p-8 space-y-4">
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            {filteredAccommodations.map((accommodation) => (
              <Card key={accommodation.id} id={`accommodation-${accommodation.id}`} className="group overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-all duration-500 bg-white h-full flex flex-col">
                <div className="relative overflow-hidden">
                  {accommodation.main_image ? (
                    <img
                      src={accommodation.main_image}
                      alt={getAccommodationDisplayName(accommodation)}
                      className="w-full h-64 sm:h-80 object-cover group-hover:scale-105 transition-transform duration-500 cursor-pointer"
                      onClick={() => openLightbox(accommodation, getAccommodationDisplayName(accommodation))}
                    />
                  ) : (
                    <div className="w-full h-64 sm:h-80 bg-gradient-to-br from-blue-100 to-blue-50 flex items-center justify-center">
                      <span className="text-blue-400 text-xl font-medium">{getAccommodationDisplayName(accommodation)}</span>
                    </div>
                  )}
                  <div className="absolute top-4 sm:top-6 right-4 sm:right-6 bg-blue-600 text-white px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-base sm:text-lg font-bold shadow-lg">
                    €{accommodation.price_per_night}
                    <span className="text-xs sm:text-sm font-normal">/night</span>
                  </div>
                </div>

                <CardContent className="p-4 sm:p-6 lg:p-8 flex-1 flex flex-col">
                  <div className="flex justify-between items-start mb-3 sm:mb-4">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900">{getAccommodationDisplayName(accommodation)}</h3>
                  </div>

                  <ExpandableDescription 
                    description={accommodation.description} 
                    maxLength={150}
                  />

                  {/* Room Details */}
                  <div className="flex flex-wrap gap-3 sm:gap-6 mb-4 sm:mb-6 text-gray-700">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />
                      <span className="font-medium text-sm sm:text-base">{accommodation.capacity}</span>
                    </div>
                    {accommodation.size && (
                      <div className="flex items-center gap-2">
                        <Maximize2 className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" />
                        <span className="font-medium text-sm sm:text-base">{accommodation.size} m²</span>
                      </div>
                    )}
                  </div>

                  {/* Amenities */}
                  <div className="mb-4 sm:mb-6">
                    <h4 className="font-semibold text-gray-900 mb-2 sm:mb-3 text-sm sm:text-base">{t('amenities')}</h4>
                    <AmenityList
                      amenityKeys={accommodation.amenities || []}
                      maxItems={6}
                      compact={true}
                      showIcons={true}
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-auto">
                    <a
                      href={`${BOOKINGS_APP_URL}/stay`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1"
                    >
                      <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base lg:text-lg py-2.5 sm:py-3">
                        Book This Accommodation
                      </Button>
                    </a>
                    <Button 
                      variant="outline" 
                      className="w-full sm:w-auto px-4 sm:px-6 text-sm sm:text-base lg:text-lg py-2.5 sm:py-3 hover:bg-blue-50 hover:text-blue-600" 
                      onClick={() => openLightbox(accommodation, getAccommodationDisplayName(accommodation))}
                    >
                      View Photos
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {filteredAccommodations.length === 0 && !loadingAccommodations && (
          <div className="text-center py-16">
            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <Users className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">No accommodations found</h3>
            <p className="text-gray-600 mb-6">Try selecting a different view type or contact us for availability.</p>
            <Link to={createPageUrl("Contact")}>
              <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                Contact Us
              </Button>
            </Link>
          </div>
        )}
      </div>
      <AccommodationLightbox
        isOpen={lightboxOpen}
        onClose={closeLightbox}
        photos={selectedAccommodationImages}
        currentIndex={currentIndex}
        onNavigate={navigateImage}
        roomName={selectedRoomName}
      />
    </div>
  );
}
